import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import meetingScorerService from '../services/meetingScorer.service';
import { apiErrorMessage } from './meetingAgendaSlice';
const initialState = {
    groups: [],
    totalDates: 0,
    page: 1,
    limit: 10,
    canManage: false,
    loading: false,
    error: null,
    clients: [],
    users: [],
    optionsLoading: false,
    saving: false,
    deleting: false,
};
/** Per-row validation errors from a 422 (ApiCall returns the AxiosError on non-2xx). */
const fieldErrors = (res) => res?.response?.data?.data?.errors ?? res?.data?.data?.errors ?? undefined;
// ══════════════════════════════════════════════════════════════════════════
// THUNKS
// ══════════════════════════════════════════════════════════════════════════
export const fetchScorerMeetings = createAsyncThunk('meetingScorer/fetchMeetings', async (args = {}, { getState, rejectWithValue }) => {
    const { silent, ...params } = args;
    try {
        const response = await meetingScorerService.listMeetings(params, getState().auth?.token || '');
        if (response?.data?.success)
            return response.data.data;
        return rejectWithValue(apiErrorMessage(response, 'Failed to load meetings'));
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to load meetings'));
    }
});
export const fetchScorerOptions = createAsyncThunk('meetingScorer/fetchOptions', async (_, { getState, rejectWithValue }) => {
    try {
        const response = await meetingScorerService.getOptions(getState().auth?.token || '');
        if (response?.data?.success)
            return response.data.data;
        return rejectWithValue(apiErrorMessage(response, 'Failed to load clients and users'));
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to load clients and users'));
    }
});
export const createScorerMeetings = createAsyncThunk('meetingScorer/createMeetings', async (meetings, { getState, rejectWithValue }) => {
    try {
        const response = await meetingScorerService.createMeetings(meetings, getState().auth?.token || '');
        if (response?.data?.success)
            return { message: response.data.message };
        return rejectWithValue({ message: apiErrorMessage(response, 'Failed to add meetings'), errors: fieldErrors(response) });
    }
    catch (err) {
        return rejectWithValue({ message: apiErrorMessage(err, 'Failed to add meetings'), errors: fieldErrors(err) });
    }
});
export const updateScorerMeeting = createAsyncThunk('meetingScorer/updateMeeting', async ({ id, payload }, { getState, rejectWithValue }) => {
    try {
        const response = await meetingScorerService.updateMeeting(id, payload, getState().auth?.token || '');
        if (response?.data?.success)
            return { message: response.data.message };
        return rejectWithValue({ message: apiErrorMessage(response, 'Failed to update the meeting'), errors: fieldErrors(response) });
    }
    catch (err) {
        return rejectWithValue({ message: apiErrorMessage(err, 'Failed to update the meeting'), errors: fieldErrors(err) });
    }
});
export const deleteScorerMeeting = createAsyncThunk('meetingScorer/deleteMeeting', async (id, { getState, rejectWithValue }) => {
    try {
        const response = await meetingScorerService.deleteMeeting(id, getState().auth?.token || '');
        if (response?.data?.success)
            return { message: response.data.message };
        return rejectWithValue(apiErrorMessage(response, 'Failed to delete the meeting'));
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to delete the meeting'));
    }
});
// ══════════════════════════════════════════════════════════════════════════
// SLICE
// ══════════════════════════════════════════════════════════════════════════
const meetingScorerSlice = createSlice({
    name: 'meetingScorer',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchScorerMeetings.pending, (state, action) => {
            if (!action.meta.arg?.silent)
                state.loading = true;
            state.error = null;
        })
            .addCase(fetchScorerMeetings.fulfilled, (state, { payload }) => {
            state.loading = false;
            state.groups = payload.groups || [];
            state.totalDates = payload.total_dates || 0;
            state.page = payload.page;
            state.limit = payload.limit;
            state.canManage = !!payload.can_manage;
        })
            .addCase(fetchScorerMeetings.rejected, (state, { payload }) => {
            state.loading = false;
            state.error = payload || 'Failed to load meetings';
        })
            .addCase(fetchScorerOptions.pending, (state) => {
            state.optionsLoading = true;
        })
            .addCase(fetchScorerOptions.fulfilled, (state, { payload }) => {
            state.optionsLoading = false;
            state.clients = payload.clients || [];
            state.users = payload.users || [];
        })
            .addCase(fetchScorerOptions.rejected, (state) => {
            state.optionsLoading = false;
        })
            .addCase(createScorerMeetings.pending, (state) => { state.saving = true; })
            .addCase(createScorerMeetings.fulfilled, (state) => { state.saving = false; })
            .addCase(createScorerMeetings.rejected, (state) => { state.saving = false; })
            .addCase(updateScorerMeeting.pending, (state) => { state.saving = true; })
            .addCase(updateScorerMeeting.fulfilled, (state) => { state.saving = false; })
            .addCase(updateScorerMeeting.rejected, (state) => { state.saving = false; })
            .addCase(deleteScorerMeeting.pending, (state) => { state.deleting = true; })
            .addCase(deleteScorerMeeting.fulfilled, (state) => { state.deleting = false; })
            .addCase(deleteScorerMeeting.rejected, (state) => { state.deleting = false; });
    },
});
// ══════════════════════════════════════════════════════════════════════════
// SELECTORS
// ══════════════════════════════════════════════════════════════════════════
export const selectScorerGroups = (s) => s.meetingScorer.groups;
export const selectScorerTotalDates = (s) => s.meetingScorer.totalDates;
export const selectScorerLoading = (s) => s.meetingScorer.loading;
export const selectScorerCanManage = (s) => s.meetingScorer.canManage;
export const selectScorerClients = (s) => s.meetingScorer.clients;
export const selectScorerUsers = (s) => s.meetingScorer.users;
export const selectScorerOptionsLoading = (s) => s.meetingScorer.optionsLoading;
export const selectScorerSaving = (s) => s.meetingScorer.saving;
export const selectScorerDeleting = (s) => s.meetingScorer.deleting;
export default meetingScorerSlice.reducer;
